<script setup lang="ts">
import {computed, onBeforeUnmount, onMounted } from "vue";
import { useStore } from "vuex";
import Length from "../../../source/js/shared/models/Geometry/Length";
import DimensionInput from "@/components/UI/DimensionInput.vue";
import ValidationMessage from "@/components/UI/ValidationMessage.vue";
import {formatForInputField, endsWithSeparator, stripNonNumeric} from "@/functions/numbers";
import {addSizeToDataLayer} from "@/functions/analytics";
import DrillHoleConfigurationFactory from "@/Models/DrillHoleConfigurations/DrillHoleConfigurationFactory";
import DrillHoleEdgeTypeCorner from "@/components/icons/DrillHoleEdgeTypeCorner.vue";
import DrillHoleEdgeTypeAround from "@/components/icons/DrillHoleEdgeTypeAround.vue";
import DrillHoleEdgeTypeTopBottom from "@/components/icons/DrillHoleEdgeTypeTopBottom.vue";
import DrillHoleEdgeTypeLeftRight from "@/components/icons/DrillHoleEdgeTypeLeftRight.vue";
import DrillHoleEdgeCounts from "@/components/DrillHoleEdgeCounts.vue";
import MessageContainer from "@/components/UI/MessageContainer.vue";

const store = useStore();

const isDrillHoleValid = computed(() => store.getters.isDrillHoleValid );
const getDrillHoleValidationMessage = computed(() => store.getters.getDrillHoleValidationMessage );
const minimumDrillHoleDiameter = computed(() => store.getters.minimumDrillHoleDiameter );
const lastGeneratedConfiguration = computed(() => store.getters.lastGeneratedConfiguration );
const drillHoleEdgeType = computed(() => store.getters.drillHoleEdgeType );
const drillHoleTypeEnabled = computed(() => store.getters.drillHoleTypeEnabled );
const getEdgeCount = computed(() => store.getters.getEdgeCount );
const drillOption = computed(() => store.getters.drillOption );

const drillHoleOffset = computed({
	get: () => formatForInputField( store.getters.drillHoleOffset.in_cm() ),
	set: (value: string) => {
		if ( ! endsWithSeparator( value ) && value !== '' ) {
			store.commit( 'updateDrillHoleOffset', Length.from_cm( stripNonNumeric( value ) ).in_mm() );
			store.commit( 'updateUsingDefaultDrillHoleDimensions', false );
		}
	},
});
const drillHoleDiameter = computed({
	get: () => formatForInputField( store.getters.drillHoleDiameter.in_cm() ),
	set: (value: string) => {
		if ( ! endsWithSeparator( value ) && value !== '' ) {
			store.commit( 'updateDrillHoleDiameter', Length.from_cm( stripNonNumeric( value ) ).in_mm() );
			store.commit( 'updateUsingDefaultDrillHoleDimensions', false );
		}
	},
});

document.addEventListener("focusin", focusChanged);

function focusChanged(event): void {
	store.commit("updateFocusedElement", event.target);
}

function drillHoleFieldsWithMessage(): {key: string, message: string}[] {
	const configuration = new DrillHoleConfigurationFactory().get_configuration( 'edge' );

	// filter out keys with valid dimensions
	const invalidKeys = configuration.get_input_keys().filter((field) => !isDrillHoleValid.value(field));

	// get the data we need to render a notice
	return invalidKeys.map((field) => {
		return {
			key: field,
			message: getDrillHoleValidationMessage.value(field),
		};
	});
}

onMounted(async () => {

	// Don't set defaults if we have an old configuration available for radial drill holes.
	if ( lastGeneratedConfiguration.value.drillholes?.type === 'edges' ) {
		return;
	}
});

onBeforeUnmount(() => {
	document.removeEventListener("focusin", focusChanged);
});

function updateDrillHoleEdgeType( type: string ) {
	store.commit( 'updateDrillHoleEdgeType', type );
}
</script>

<template>
	<div>
		<div class="measurements-selector__sub-section">
			<p class="label">{{ $i18n.drillHolesChoosePosition }}</p>
			<div class="measurements-selector__menu">

				<button
					v-if="drillHoleTypeEnabled('corners')"
					type="button"
					v-on:click="updateDrillHoleEdgeType('corners');"
					v-bind:class='{
						"measurements-selector__menu-button": true,
						"measurements-selector__menu-button--wide": true,
						"measurements-selector__menu-button-drill-edge-type-corners": true,
						"measurements-selector__menu-button--active": drillHoleEdgeType === "corners",
					}'
				>
					<drill-hole-edge-type-corner alt='' />
					{{ $i18n.drillHoleEdgeTypes.corners }}
				</button>
				<button
					v-if="drillHoleTypeEnabled('around')"
					type="button"
					v-on:click="updateDrillHoleEdgeType('around');"
					v-bind:class='{
						"measurements-selector__menu-button": true,
						"measurements-selector__menu-button--wide": true,
						"measurements-selector__menu-button-drill-edge-type-around": true,
						"measurements-selector__menu-button--active": drillHoleEdgeType === "around",
					}'
				>
					<drill-hole-edge-type-around alt='' />
					{{ $i18n.drillHoleEdgeTypes.around }}
				</button>
				<button
					v-if="drillHoleTypeEnabled('top_bottom')"
					type="button"
					v-on:click="updateDrillHoleEdgeType('top_bottom');"
					v-bind:class='{
						"measurements-selector__menu-button": true,
						"measurements-selector__menu-button--wide": true,
						"measurements-selector__menu-button-drill-edge-type-top_bottom": true,
						"measurements-selector__menu-button--active": drillHoleEdgeType === "top_bottom",
					}'
				>
					<drill-hole-edge-type-top-bottom alt='' />
					{{ $i18n.drillHoleEdgeTypes.top_bottom }}
				</button>
				<button
					v-if="drillHoleTypeEnabled('left_right')"
					type="button"
					v-on:click="updateDrillHoleEdgeType('left_right');"
					v-bind:class='{
						"measurements-selector__menu-button": true,
						"measurements-selector__menu-button--wide": true,
						"measurements-selector__menu-button-drill-edge-type-left_right": true,
						"measurements-selector__menu-button--active": drillHoleEdgeType === "left_right",
					}'
				>
					<drill-hole-edge-type-left-right alt='' />
					{{ $i18n.drillHoleEdgeTypes.left_right }}
				</button>
			</div>
		</div>

		<div class="input-container" v-if="drillHoleEdgeType !== null">
			<div class="drill-hole-inputs">
				<dimension-input
					inputRef="drillHoleOffset"
					id="drillHoleOffset"
					name="drillHoleOffset"
					inputmode="decimal"
					unit="cm"
					:label="$i18n.drillHoleConfiguration.distance_from_edge"
					:invalid="!isDrillHoleValid('drillHoleOffset')"
					:onBlur="() => addSizeToDataLayer($i18n.drillHoleConfiguration.distance_from_edge, drillHoleOffset, 'drill-hole-offset')"
					v-model="drillHoleOffset"
				/>

				<dimension-input
					inputRef="drillHoleDiameter"
					id="drillHoleDiameter"
					name="drillHoleDiameter"
					inputmode="decimal"
					unit="cm"
					:min="minimumDrillHoleDiameter"
					:label="`${$i18n.drillHoleConfiguration.diameter_drill_hole}`"
					:invalid="!isDrillHoleValid('drillHoleDiameter')"
					:onBlur="() => addSizeToDataLayer($i18n.drillHoleConfiguration.diameter_drill_hole, drillHoleDiameter, 'drill-hole-diameter')"
					v-model="drillHoleDiameter"
				/>

				<drill-hole-edge-counts v-if="drillHoleTypeEnabled('around') && drillHoleEdgeType === 'around' && getEdgeCount" />
			</div>

			<message-container>
				<validation-message
					v-if="drillOption !== 'none'"
					type="notice"
				>
					{{ $i18n.drillHolesNotice }}
				</validation-message>

				<validation-message
					v-for="field in drillHoleFieldsWithMessage()"
					:key="field.key + '-validation-message'"
					:input-id="field.key"
				>
					{{ field.message }}
				</validation-message>
			</message-container>
		</div>
	</div>
</template>

<style scoped lang="scss">
.input-container {
	background-color: $brand-blue-background;
	border-radius: 8px;
	padding: 20px;
}

.drill-hole-inputs {
	display: grid;
	grid-template-columns: repeat(2, 1fr);
	gap: 12px;
}

.label {
	@include font-size(14px, 16px);
	color: var(--text-color);
	font-weight: 700;
	margin-bottom: 10px;
}

</style>
